import {
  ShaderMount,
  waterFragmentShader,
  getShaderColorFromString,
  ShaderFitOptions,
  emptyPixel,
} from "@paper-design/shaders"

const SELECTOR = "[data-water-shader]"
const MAX_PIXEL_COUNT = 3_200_000

function buildUniforms(image) {
  return {
    u_image: image,
    u_colorBack: getShaderColorFromString("#e9dec9"),
    u_colorHighlight: getShaderColorFromString("#ffffff"),
    u_highlights: 0.2,
    u_layering: 0.5,
    u_edges: 0.8,
    u_waves: 0.3,
    u_caustic: 0.1,
    u_size: 2,
    u_fit: ShaderFitOptions.contain,
    u_scale: 0.55,
    u_rotation: -8,
    u_offsetX: 0.43,
    u_offsetY: 0.04,
    u_originX: 0.55,
    u_originY: 0.8,
    u_worldWidth: 0,
    u_worldHeight: 0,
  }
}

export function mountShader(element, image) {
  if (element.paperShaderMount) return

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches

  let mount
  try {
    mount = new ShaderMount(
      element,
      waterFragmentShader,
      buildUniforms(image),
      undefined,
      prefersReducedMotion ? 0 : 1,
      0,
      1,
      MAX_PIXEL_COUNT,
    )
  } catch {
    return
  }

  let disposed = false
  let observer
  if (!prefersReducedMotion && "IntersectionObserver" in window) {
    observer = new IntersectionObserver(
      (entries) => {
        if (disposed) return

        for (const entry of entries) {
          mount.setSpeed(entry.isIntersecting ? 1 : 0)
        }
      },
      { threshold: 0 },
    )
    observer.observe(element)
  }

  const cleanup = (event) => {
    if (event.persisted || disposed) return

    disposed = true
    observer?.disconnect()
    mount.dispose()
  }
  window.addEventListener("pagehide", cleanup, { once: true })
}

function loadImage(src, crossOrigin) {
  return new Promise((resolve, reject) => {
    const image = new Image()
    if (crossOrigin) image.crossOrigin = "anonymous"
    image.addEventListener("load", () => resolve(image), { once: true })
    image.addEventListener("error", reject, { once: true })
    image.src = src
  })
}

function supportsWebGl() {
  try {
    return Boolean(document.createElement("canvas").getContext("webgl2"))
  } catch {
    return false
  }
}

async function mountElement(element) {
  if (navigator.webdriver || !supportsWebGl()) return

  const src = element.dataset.waterImage
  let image
  try {
    image = src
      ? await loadImage(src, true)
      : await loadImage(emptyPixel, false)
  } catch {
    image = await loadImage(emptyPixel, false)
  }
  mountShader(element, image)
}

function init() {
  document.querySelectorAll(SELECTOR).forEach(mountElement)
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init)
} else {
  init()
}
